// Russian for the audit-trail namespace (spec 16 §2.16/§4) — the design
// partner's working language, and the language the person who opens this screen
// works in. The Big Boss at KazKabel is the reader here, and an English-only
// compliance record is a record he cannot put in front of an auditor or a
// customer's quality department.
//
// REGISTER: аудит/журнал аудита for the record itself, цепочка and
// хеш/отпечаток for the tamper-evidence, запись for one entry — the vocabulary
// a Kazakh plant's ISO 9001 documentation already uses, not generic UI Russian.
//
// THE TWO NOUNS STAY TWO NOUNS, exactly as in English: «журнал аудита» is the
// RECORD and «цепочка» is the tamper-evidence over it, because the screen makes
// two different claims and merging them would let «страница сходится» read as
// «история цела».
//
// SCOPE WORDS ARE LOAD-BEARING: «на этой странице» and «с начала» are not
// filler. They are the difference between what was checked and what was not,
// and dropping them would make the Russian screen claim more than the English
// one does.

export const auditRu = {
  title: "Журнал аудита",
  subtitle: "Все изменения в этом рабочем пространстве по порядку, защищённые от подделки.",
  nav: { trail: "Журнал аудита" },

  filters: {
    legend: "Фильтр журнала",
    entityType: "Тип объекта",
    entityId: "Объект",
    entityIdPlaceholder: "Нажмите на объект в списке",
    actor: "Кто",
    actorPlaceholder: "Нажмите на имя в списке",
    actionPrefix: "Раздел",
    actionPrefixAny: "Все разделы",
    from: "С",
    to: "По",
    clear: "Сбросить фильтры",
    entityIdNeedsType: "Укажите тип объекта — идентификаторы не уникальны между типами.",
    entityTypeAny: "Все типы объектов",
  },

  area: {
    identity: "Люди и доступ",
    catalogue: "Каталог",
    onboarding: "Настройка",
    inventory: "Склад",
    production: "Производство",
    quality: "Качество",
    waste: "Отходы",
    files: "Вложения",
    agent: "Агенты",
    sales: "Продажи",
    messaging: "Сообщения",
    planning: "Планирование",
    other: "Прочее",
  },

  col: {
    seq: "№",
    when: "Когда",
    who: "Кто",
    action: "Что произошло",
    entity: "Объект",
    payload: "Подробности",
  },

  actorKind: {
    system: "Система",
    anonymous: "Не авторизован",
    platform_staff: "Поддержка Assessly",
    unknown: "Не распознан",
  },

  row: {
    noEntity: "—",
    payloadRedacted: "Недоступно для вашей роли",
    payloadRedactedRequires: "Нужен дополнительный доступ.",
    payloadUnmapped: "Для записей этого вида не показывается",
    payloadHidden: "Скрыто",
    payloadHiddenNeeds:
      "Строки с пометкой «Не показано» требуют доступа, которого нет у вашей роли.",
    payloadHiddenUnmapped:
      "Строки «Скрыто» — записи такого вида, их сведения не показываются ни одной роли.",
    payloadEmpty: "Ничего не записано",
    show: "Показать подробности",
    hide: "Скрыть подробности",
  },

  chain: {
    heading: "Целостность",
    pageOk:
      "{{rows}} записей на этой странице не изменены, и {{links}} связей между ними сходятся.",
    pageBroken: "Последняя порция не сходится — запись {{seq}} ({{reason}}).",
    pageEmpty: "В последней порции записей нет, проверять нечего.",
    pageScopeNote:
      "Это только о последней загруженной порции — не о записях выше и не о всей цепочке. Из-за фильтров порция — это выборка, поэтому у большинства записей нет соседа для проверки связи.",
    notAnchored:
      "В последней порции нет первой записи, поэтому ничто здесь не доказывает, с чего журнал начался.",
    verifyHeading: "Вся цепочка",
    verifyOk: "Проверено с начала: {{length}} из {{head}} записей.",
    verifyPartial: "Проверено записей: {{length}}, до № {{to}} из {{head}}.",
    verifyBroken: "Цепочка нарушена на записи № {{seq}} ({{reason}}).",
    verifyEmpty: "Пока ничего не записано.",
    verifyRunning: "Проверка…",
    verifyError: "Не удалось проверить цепочку.",
    reason: {
      seq_gap: "запись отсутствует",
      prev_hash_mismatch: "связь с предыдущей записью не совпадает",
      curr_hash_mismatch: "запись не совпадает со своим отпечатком",
      unknown: "причина неизвестна",
    },
  },

  loading: "Загрузка журнала аудита",
  loadMore: "Показать ещё",
  retry: "Повторить",
  requestId: "Идентификатор запроса",

  errorTitle: "Не удалось загрузить журнал аудита",
  errorBody: "Журнал не вернулся. Попробуйте ещё раз через минуту.",

  emptyTitle: "Совпадений нет",
  emptyBody: "По этим фильтрам записей нет. Расширьте период или сбросьте фильтры.",
  emptyEntityTitle: "По этому объекту записей нет",
  emptyEntityBody:
    "В журнал попадают изменения, сделанные в Assessly. У объекта, созданного импортом или загрузкой данных, истории не будет, пока его здесь не изменят.",

  refusedTitle: "Не ваш экран",
  refusedBody:
    "Журнал аудита — для владельцев и администраторов. Обратитесь к администратору группы, если он вам нужен.",

  scopeRefusedTitle: "Нужен доступ по группе",
  scopeRefusedBody:
    "Журнал аудита охватывает всю компанию и требует доступа на уровне группы. Попросите администратора группы расширить ваш доступ.",

  offlineTitle: "Нет сети",
  offlineBody: "Журнал аудита загрузится, когда связь восстановится.",

  entityHeading: "История этого объекта",
  entityBackToAll: "Показать весь журнал",
  entityLink: "Полный журнал аудита",
  verb: {
    accepted: "принято",
    activated: "активировано",
    added: "добавлено",
    adjusted: "скорректировано",
    applied: "применено",
    approved: "утверждено",
    archived: "архивировано",
    attached: "прикреплено",
    cancelled: "отменено",
    changed: "изменено",
    closed: "закрыто",
    completed: "завершено",
    confirmed: "подтверждено",
    connected: "подключено",
    converted: "преобразовано",
    created: "создано",
    deactivated: "деактивировано",
    decided: "принято решение",
    declined: "отказано",
    deleted: "удалено",
    delivered: "доставлено",
    detached: "снято",
    disabled: "отключено",
    discarded: "отброшено",
    disconnected: "отсоединено",
    dispatched: "отправлено",
    enabled: "включено",
    ended: "окончено",
    expired: "истекло",
    failed: "не удалось",
    firmed: "зафиксировано",
    flagged: "отмечено",
    generated: "сформировано",
    imported: "импортировано",
    installed: "установлено",
    invited: "приглашение отправлено",
    issued: "выдано",
    linked: "связано",
    logged: "записано",
    lost: "проиграно",
    merged: "объединено",
    opened: "открыто",
    packed: "упаковано",
    picked: "отобрано",
    placed: "поставлено",
    posted: "проведено",
    proposed: "предложено",
    published: "опубликовано",
    quarantined: "помещено в карантин",
    received: "получено",
    recorded: "записано",
    registered: "зарегистрировано",
    rejected: "отклонено",
    released: "выпущено",
    removed: "убрано",
    renamed: "переименовано",
    reopened: "открыто повторно",
    replaced: "заменено",
    reported: "внесено",
    requested: "запрошено",
    resolved: "решено",
    restored: "восстановлено",
    reversed: "сторнировано",
    revised: "пересмотрено",
    revoked: "отозвано",
    saved: "сохранено",
    sent: "отправлено",
    shipped: "отгружено",
    started: "начато",
    submitted: "отправлено на утверждение",
    updated: "обновлено",
    upserted: "сохранено",
    verified: "проверено",
    waived: "снято",
    withdrawn: "снято с рассмотрения",
    won: "выиграно",
  },
  entity: {
    tenant: "Рабочее пространство",
    user: "Пользователь",
    facility: "Площадка",
    department: "Подразделение",
    work_center: "Рабочий центр",
    product: "Продукт",
    bom: "Спецификация",
    routing: "Маршрут",
    catalogue_template: "Шаблон продукта",
    uom: "Единица измерения",
    cost_run: "Расчёт себестоимости",
    attribute_schema: "Атрибуты продукта",
    import_job: "Импорт",
    onboarding_session: "Настройка",
    onboarding_step_state: "Шаг настройки",
    go_live_checklist_item: "Проверка перед запуском",
    terminology_override: "Термин",
    vertical_pack_install: "Отраслевой пакет",
    qr_print_batch: "QR-этикетки",
    stock_unit: "Складская единица",
    lot: "Партия",
    location: "Место хранения",
    cycle_count: "Пересчёт",
    adjustment_request: "Запрос на корректировку",
    inventory_settings: "Настройки склада",
    work_order: "Заказ на производство",
    shift_definition: "Смена",
    downtime_log: "Простой",
    downtime_reason: "Причина простоя",
    floor_device: "Устройство цеха",
    qc_inspection: "Проверка",
    qc_hold: "Блокировка по качеству",
    qc_control_point: "Точка контроля",
    qc_failure_cause: "Причина брака",
    qc_cut_event: "Вырезка по качеству",
    waste_event: "Запись об отходах",
    waste_category: "Категория отходов",
    waste_cause_code: "Причина отходов",
    remnant: "Остаток",
    remnant_price_class: "Ценовая группа остатков",
    waste_settings: "Настройки отходов",
    customer: "Клиент",
    customer_contact: "Контакт",
    contact_channel: "Канал связи",
    quote: "Коммерческое предложение",
    sales_order: "Заказ клиента",
    sales_shipment: "Отгрузка",
    sales_opportunity: "Сделка",
    sales_activity: "Активность",
    price_list: "Прайс-лист",
    planned_order: "Плановый заказ",
    mps_plan: "Главный календарный план",
    schedule: "График",
    mrp_run: "План потребности в материалах",
    forecast: "Прогноз",
    conversation: "Переписка",
    message: "Сообщение",
    message_template: "Шаблон сообщения",
    file: "Файл",
  },
  action: {
    catalogue: {
      product_created: "Продукт создан",
      product_updated: "Продукт изменён",
      product_activated: "Продукт активирован",
      bom_activated: "Спецификация введена в действие",
      routing_activated: "Маршрут введён в действие",
      cost_run_released: "Себестоимость утверждена",
    },
    identity: {
      invite_created: "Отправлено приглашение",
      invite_accepted: "Приглашение принято",
      member_provisioned: "Сотрудник добавлен",
      member_role_changed: "Роль изменена",
      member_revoked: "Доступ отозван",
      facility_created: "Площадка добавлена",
      work_center_created: "Рабочий центр добавлен",
    },
    onboarding: {
      went_live: "Завод запущен",
      import_committed: "Импорт сохранён",
      pack_installed: "Отраслевой пакет установлен",
      step_completed: "Шаг настройки завершён",
      checklist_item_waived: "Проверка перед запуском снята",
    },
    inventory: {
      movement_recorded: "Движение запаса записано",
      count_posted: "Пересчёт проведён",
      transfer_dispatched: "Перемещение отправлено",
      transfer_received: "Перемещение получено",
      lot_quarantined: "Партия помещена в карантин",
      lot_released: "Партия освобождена",
      adjustment_request_decided: "Решение по корректировке принято",
    },
    production: {
      work_order_created: "Заказ на производство создан",
      work_order_released: "Заказ на производство выпущен",
      work_order_completed: "Заказ на производство выполнен",
      work_order_closed: "Заказ на производство закрыт",
      operation_started: "Операция начата",
      operation_completed: "Операция завершена",
      qty_reported: "Выпуск внесён",
      downtime_logged: "Простой записан",
      shift_closed: "Смена закрыта",
      cost_adjusted: "Затраты скорректированы",
    },
    quality: {
      inspection_completed: "Проверка завершена",
      inspection_result_recorded: "Результат проверки записан",
      hold_placed: "Блокировка установлена",
      hold_released: "Блокировка снята",
    },
    waste: {
      event_recorded: "Отходы записаны",
      remnant_registered: "Остаток зарегистрирован",
    },
    sales: {
      customer_created: "Клиент создан",
      customer_updated: "Клиент изменён",
    },
    sales_quote: {
      created: "Коммерческое предложение создано",
      sent: "Коммерческое предложение отправлено",
      revised: "Коммерческое предложение пересмотрено",
    },
    sales_order: {
      created: "Заказ клиента создан",
      cancelled: "Заказ клиента отменён",
    },
    sales_shipment: {
      shipped: "Отгрузка отправлена",
    },
    sales_opportunity: {
      won: "Сделка выиграна",
      lost: "Сделка проиграна",
    },
    planned_order: {
      firmed: "Плановый заказ зафиксирован",
      converted: "Плановый заказ преобразован в заказ на производство",
    },
    schedule: {
      published: "График опубликован",
    },
    mps_plan: {
      approved: "Главный календарный план утверждён",
    },
  },
  actionPhrase: "{{area}} — {{verb}}",
  actionOther: "{{area}} — изменено",
  entityOther: "Запись",
};
